/**
 * 개인 주간 보고 화면. 항목은 다시 계산하고, 빼기와 메모만 저장한다.
 */
import { Caption1, Checkbox, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { ArrowLeft16Regular, ArrowRight16Regular, Copy16Regular, Print16Regular } from "@fluentui/react-icons";
import { useMemo, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { useDataTableStyles } from "@/components/data-table";
import { FuiButton, FuiDropdown, FuiInput } from "@/components/fui-control";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { ListToolbar } from "@/components/list-toolbar";
import { SectionTitle } from "@/components/ui-typography";
import { todayISO } from "@/engine/date";
import type { Project, UserResource } from "@/engine/types";
import { SegmentedControl } from "@/features/resources/SegmentedControl";
import { formatDateRange } from "@/lib/date-format";
import { fmtMM } from "@/lib/format";
import { contentShell, shellGutter } from "@/shell/content-frame";
import { useCatalogStore } from "@/store/catalog-store";
import { useProjectStore } from "@/store/project-store";
import {
  actorsInProject,
  buildUserWeekly,
  formatUserWeeklyMarkdown,
  formatUserWeeklyPlain,
  shiftWeek,
  toggleWeeklyExclusion,
  weekWindowContaining,
  weeklyNoteKey,
  type UserWeeklyItem,
  type UserWeeklyReport,
  type WeeklyActor,
  type WeeklyBucket,
} from "./user-weekly-model";

const PRINT = `@media print { @page { size: A4; margin: 12mm; } [data-print-hide] { display: none !important; } }`;

const BUCKETS: { id: WeeklyBucket; label: string }[] = [
  { id: "done", label: "1. 금주 완료" },
  { id: "inProgress", label: "2. 진행 중" },
  { id: "next", label: "3. 차주 계획" },
  { id: "blockers", label: "4. 지연/블로커" },
];

const useStyles = makeStyles({
  page: {
    ...contentShell,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  fill: {
    flex: 1,
    minHeight: 0,
    display: "flex",
  },
  embedded: {
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    gap: tokens.spacingVerticalS,
  },
  tools: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
  },
  assignee: {
    width: "160px",
    flex: "0 0 160px",
  },
  body: {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
    padding: `0 ${shellGutter} ${tokens.spacingVerticalXXL}`,
    boxSizing: "border-box",
  },
  embeddedBody: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
    minWidth: 0,
  },
  plans: {
    color: tokens.colorNeutralForeground2,
    fontVariantNumeric: "tabular-nums",
  },
  memo: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    maxWidth: "640px",
  },
  group: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  excluded: {
    color: tokens.colorNeutralForeground3,
  },
  quiet: {
    color: tokens.colorNeutralForeground3,
  },
  check: {
    width: "36px",
    textAlign: "center",
  },
  meta: {
    width: "120px",
    color: tokens.colorNeutralForeground2,
  },
  toast: {
    position: "fixed",
    left: "50%",
    bottom: "24px",
    transform: "translateX(-50%)",
    zIndex: 20,
    padding: "6px 10px",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
  },
});

function useCopyNotice() {
  const styles = useStyles();
  const [notice, setNotice] = useState("");
  const copy = (text: string) => {
    const done = (ok: boolean) => {
      setNotice(ok ? "복사했습니다" : "복사하지 못했습니다");
      window.setTimeout(() => setNotice(""), 2000);
    };
    const clipboard = navigator.clipboard;
    if (!clipboard) {
      done(false);
      return;
    }
    void clipboard.writeText(text).then(() => done(true), () => done(false));
  };
  const node = notice ? (
    <div className={styles.toast} role="status" data-print-hide="">
      {notice}
    </div>
  ) : null;
  return { copy, node };
}

export function WeeklyScopeSwitch() {
  const [params, setParams] = useSearchParams();
  const scope = params.get("scope") === "assignee" ? "assignee" : "plan";
  return (
    <SegmentedControl
      ariaLabel="주간 보고 범위"
      value={scope}
      onChange={(value) => {
        const next = new URLSearchParams(params);
        if (value === "assignee") next.set("scope", "assignee");
        else {
          next.delete("scope");
          next.delete("who");
        }
        setParams(next, { replace: true });
      }}
      options={[
        { value: "plan", label: "프로젝트 총괄" },
        { value: "assignee", label: "담당자별" },
      ]}
    />
  );
}

function planLine(report: UserWeeklyReport): string {
  if (!report.plans.length) return "";
  return report.plans
    .map((plan) => (plan.allocationPct == null ? plan.projectName : `${plan.projectName} ${plan.allocationPct}%`))
    .join(" · ");
}

function kpiLine(report: UserWeeklyReport): string {
  const { kpi } = report;
  return `완료 ${kpi.done} · 진행 ${kpi.inProgress} · 차주 ${kpi.next} · 해결 ${kpi.resolvedIssues} · ${fmtMM(kpi.plannedMM)}`;
}

export function UserWeeklyPanel({
  title,
  actor,
  projects,
  live,
  resources,
  sections,
  filters,
  embedded = false,
}: {
  title: string;
  actor: WeeklyActor;
  projects: Record<string, Project>;
  live: Project | null;
  resources: Record<string, UserResource>;
  sections?: ReactNode;
  filters?: ReactNode;
  embedded?: boolean;
}) {
  const styles = useStyles();
  const table = useDataTableStyles();
  const { copy, node } = useCopyNotice();
  const notes = useCatalogStore((s) => s.userWeekly);
  const setNote = useCatalogStore((s) => s.setUserWeeklyNote);
  const today = todayISO();
  const [week, setWeek] = useState(() => weekWindowContaining(today));
  const key = weeklyNoteKey(actor.name, week.start);
  const note = notes[key];
  const report = useMemo(
    () =>
      buildUserWeekly({
        projects: Object.values(projects),
        live,
        actor,
        resources,
        week,
        today,
        note,
      }),
    [projects, live, actor, resources, week, today, note],
  );
  const range = formatDateRange(week.start, week.end, "numeric");
  const write = (next: typeof note) => setNote(key, next ?? { excludedIds: [], memo: "" });

  const tools = (
    <div className={styles.tools} data-print-hide="">
      <FuiButton appearance="subtle" icon={<ArrowLeft16Regular />} aria-label="이전 주" onClick={() => setWeek((current) => shiftWeek(current, -1))}>
        이전 주
      </FuiButton>
      <Caption1>{range}</Caption1>
      <FuiButton appearance="subtle" icon={<ArrowRight16Regular />} iconPosition="after" aria-label="다음 주" onClick={() => setWeek((current) => shiftWeek(current, 1))}>
        다음 주
      </FuiButton>
      <FuiButton appearance="primary" icon={<Copy16Regular />} onClick={() => copy(formatUserWeeklyMarkdown(report))}>
        복사
      </FuiButton>
      <FuiButton appearance="subtle" onClick={() => copy(formatUserWeeklyPlain(report))}>
        텍스트
      </FuiButton>
      <FuiButton appearance="subtle" icon={<Print16Regular />} onClick={() => window.print()}>
        인쇄
      </FuiButton>
    </div>
  );

  const body = (
    <>
      {planLine(report) ? <Caption1 className={styles.plans}>{planLine(report)}</Caption1> : null}
      {BUCKETS.map((bucket) => (
        <section key={bucket.id} className={styles.group} aria-label={bucket.label}>
          <SectionTitle>{bucket.label}</SectionTitle>
          <BucketTable
            styles={styles}
            table={table}
            items={report.items.filter((item) => item.bucket === bucket.id)}
            onToggle={(id) => write(toggleWeeklyExclusion(note ?? { excludedIds: [], memo: report.memo }, id))}
          />
        </section>
      ))}
      <label className={styles.memo}>
        <Caption1>5. 특이사항</Caption1>
        <FuiInput
          aria-label="특이사항"
          value={report.memo}
          placeholder="한 줄"
          onChange={(_, data) => write({ excludedIds: note?.excludedIds ?? [], memo: data.value })}
        />
      </label>
    </>
  );

  if (!report.plans.length) {
    const empty = <GhostEmptyState title="이번 주 보고에 올릴 일이 없습니다" description="맡은 플랜의 완료와 진행이 여기에 모입니다." />;
    if (embedded) {
      return (
        <div className={styles.embedded}>
          <style>{PRINT}</style>
          {tools}
          {empty}
          {node}
        </div>
      );
    }
    return (
      <div className={styles.page}>
        <style>{PRINT}</style>
        <div data-print-hide="">
          <ListToolbar title={title} sections={sections} filters={filters} headingAction={tools} />
        </div>
        <div className={styles.fill}>{empty}</div>
        {node}
      </div>
    );
  }

  if (embedded) {
    return (
      <div className={styles.embedded}>
        <style>{PRINT}</style>
        {tools}
        <div className={styles.embeddedBody}>{body}</div>
        {node}
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <style>{PRINT}</style>
      <div data-print-hide="">
        <ListToolbar title={title} description={kpiLine(report)} sections={sections} filters={filters} headingAction={tools} />
      </div>
      <div className={styles.body}>{body}</div>
      {node}
    </div>
  );
}

function BucketTable({
  styles,
  table,
  items,
  onToggle,
}: {
  styles: ReturnType<typeof useStyles>;
  table: ReturnType<typeof useDataTableStyles>;
  items: UserWeeklyItem[];
  onToggle: (id: string) => void;
}) {
  if (!items.length) return <Caption1 className={styles.quiet}>없음</Caption1>;
  return (
    <div className={table.frame}>
      <table className={table.table}>
        <thead>
          <tr>
            <th scope="col" className={mergeClasses(table.th, styles.check)}>
              빼기
            </th>
            <th scope="col" className={table.th}>
              플랜
            </th>
            <th scope="col" className={table.th}>
              항목
            </th>
            <th scope="col" className={mergeClasses(table.th, styles.meta)}>
              구분
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id} className={item.excluded ? styles.excluded : undefined}>
              <td className={mergeClasses(table.td, styles.check)}>
                <Checkbox
                  checked={item.excluded}
                  aria-label="보고서에서 빼기"
                  onChange={() => onToggle(item.id)}
                />
              </td>
              <td className={table.td} title={item.projectName}>
                <span className={table.clip}>{item.projectName}</span>
              </td>
              <td className={table.td} title={item.title}>
                <span className={table.clip}>{item.title}</span>
              </td>
              <td className={mergeClasses(table.td, styles.meta)}>{item.meta}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** 홈의 한 줄. 이번 주 완료와 진행, 그리고 슬랙 복사. */
export function UserWeeklyHomeLine({
  projects,
  live,
  actor,
  resources,
}: {
  projects: Record<string, Project>;
  live: Project | null;
  actor: WeeklyActor;
  resources: Record<string, UserResource>;
}) {
  const styles = useStyles();
  const { copy, node } = useCopyNotice();
  const notes = useCatalogStore((s) => s.userWeekly);
  const today = todayISO();
  const week = weekWindowContaining(today);
  const report = useMemo(
    () =>
      buildUserWeekly({
        projects: Object.values(projects),
        live,
        actor,
        resources,
        week,
        today,
        note: notes[weeklyNoteKey(actor.name, week.start)],
      }),
    [projects, live, actor, resources, week, today, notes],
  );
  if (!report.plans.length) return null;
  return (
    <div className={styles.tools}>
      <Caption1 className={styles.plans}>
        이번 주 완료 {report.kpi.done}건 · 진행 {report.kpi.inProgress}건
      </Caption1>
      <FuiButton appearance="subtle" icon={<Copy16Regular />} onClick={() => copy(formatUserWeeklyMarkdown(report))}>
        복사
      </FuiButton>
      {node}
    </div>
  );
}

/** 플랜 주간 보고의 담당자별. 그 플랜 안에서만 모은다. */
export function PlanAssigneeWeekly() {
  const styles = useStyles();
  const catalog = useCatalogStore((s) => s.projects);
  const resources = useCatalogStore((s) => s.resources);
  const activeId = useProjectStore((s) => s.activeId);
  const open = useProjectStore((s) => s.project);
  const [params, setParams] = useSearchParams();
  const stored = activeId ? catalog[activeId] ?? null : null;
  const live = activeId && open.id === activeId ? open : null;
  const plan = live ?? stored;
  const actors = useMemo(() => (plan ? actorsInProject(plan, resources) : []), [plan, resources]);
  const requested = params.get("who");
  const actor = actors.find((row) => row.name === requested) ?? actors[0] ?? null;
  const projects = useMemo(() => {
    if (!plan) return {};
    const base = stored ?? plan;
    return { [plan.id]: base };
  }, [plan, stored]);

  if (!plan || !actor) {
    return (
      <div className={styles.page}>
        <ListToolbar title="주간 보고" filters={<WeeklyScopeSwitch />} />
        <GhostEmptyState title="담당자가 없습니다" description="배정이나 담당이 있으면 그 사람의 주가 여기에 모입니다." />
      </div>
    );
  }

  return (
    <UserWeeklyPanel
      title="주간 보고"
      actor={actor}
      projects={projects}
      live={live}
      resources={resources}
      filters={
        <>
          <WeeklyScopeSwitch />
          <FuiDropdown
            className={styles.assignee}
            aria-label="담당자"
            value={actor.name}
            options={actors.map((row) => ({ value: row.name, label: row.name }))}
            onValueChange={(value) => {
              const next = new URLSearchParams(params);
              next.set("scope", "assignee");
              next.set("who", value);
              setParams(next, { replace: true });
            }}
          />
        </>
      }
    />
  );
}
